import React, { useState } from "react"
import Navbar from "./components/navbar.tsx"
import Home from "./pages/Home.tsx"
import Search from "./pages/ProductSearch.tsx"
import RAGKnowledgebase from "./pages/RAGKnowledgebase.tsx"
import SchemaMapper from "./pages/Schemamapper.tsx"
import TwilioCallApp from "./pages/InvisileAI.tsx"
import CampaignPage from "./pages/CampaignPage.tsx"
import SessionLogs from "./pages/logs.tsx"
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ToastProvider } from "./components/hooks/use-toast";
import { Toaster } from "./components/CampaignComponents/Toaster";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import AnalyticsDashboard from "./pages/AnalyticsDashboard.tsx"
import { Header } from "./components/Header";
import { Login } from "./pages/Login";
import  CalculatorApp  from "./pages/calculator.tsx"; // Import the calculator component from "lucide-react"
import Dashboard from "./pages/CampaignAnalytics.tsx"
import AlmajdouieDashboard from "./pages/Almajdouie/Almajdouie_Analytics.tsx";
import AlmajdouieCallDashboard from "./pages/Almajdouie/AlmajdouieCallAnalytics.tsx";
import AlMajdouieCallApp from "./pages/Almajdouie/AlMajdouie_call.tsx";
import LeadAnalyticsDashboard from "./pages/Lead/LeadCallAnalytics.tsx";
import LeadDashboard from "./pages/Lead/LeadCampaignAnalytics.tsx";
import LeadCallApp from "./pages/Lead/LeadCall.tsx";
import ServiceSlotsPage from './pages/Almajdouie/ServiceSlotsPage.tsx'; // Adjust path if needed
import PedalCallApp from './pages/Pedal/PedalCall.tsx'; // Import the Pedal Call component
import PedalDashboard from './pages/Pedal/PedalCampaignAnalytics.tsx'; // Import the Pedal Campaign Analytics component
import PedalAnalyticsDashboard from './pages/Pedal/PedalCallAnalytics.tsx'; // Import
import OrderCallApp from "./pages/MaiDubai/MaiDubaiCall.tsx";
import CustomerAnalyticsDashboard from "./pages/MaiDubai/MaiDubaiCallAnalytics.tsx";

const queryClient = new QueryClient();

const App = () => {
  const [isNavOpen, setIsNavOpen] = useState(false);
  const [navbarValues, setNavbarValues] = useState({
    apiKey: "",
    selectedOption: "",
  });
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  // State to store the user's role
  const [userRole, setUserRole] = useState<string | null>(null);

  /**
   * Handles the login process, setting authentication status and user role.
   * @param role The role of the logged-in user (e.g., "admin", "almajdouie").
   */
  const handleLogin = (role: string) => {
    setIsAuthenticated(true);
    setUserRole(role);
  };

  /**
   * Handles the logout process, resetting authentication status and user role.
   */
  const handleLogout = () => {
    setIsAuthenticated(false);
    setUserRole(null);
  };

  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <BrowserRouter>
          {isAuthenticated ? (
            <div className="flex flex-col min-h-screen bg-gray-100 font-sans">
              {/* Header spans the full width */}
              <Header
                user={{
                  name:
                    userRole === 'admin'
                      ? "Admin"
                      : userRole === 'leadAssistance'
                      ? "Lead Assistance"
                      : userRole === 'PedalAssistance'
                      ? "Pedal Assistance"
                      : userRole === 'MaiDubaiAssistance'
                      ? "Mai Dubai Assistance"
                      : "Almajdouie User",
                  email:
                    userRole === 'admin'
                      ? "autoconnectai@oorjit.com"
                      : userRole === 'leadAssistance'
                      ? "leadassistance@oorjit.com"
                      : userRole === 'PedalAssistance'
                      ? "pedalassistance@oorjit.com"
                      : userRole === 'MaiDubaiAssistance'
                      ? "maidubaiassistance@oorjit.com"
                      : "almajdouie@oorjit.com"
                }}
                onLogout={handleLogout}
              />

              {/* Main content area with navbar and page content */}
              <div className="flex flex-1 overflow-hidden">
                {/* Navbar - Now passing userRole */}
                <Navbar
                  isOpen={isNavOpen}
                  toggleMenu={() => setIsNavOpen(!isNavOpen)}
                  onValuesChange={(values) => setNavbarValues(values)}
                  userRole={userRole}
                />

                {/* Main content area */}
                <main className={`flex-1 overflow-auto transition-all duration-300 pt-[77px] ${isNavOpen ? "ml-[261px]" : "ml-[53px]"}`}>
                  <Routes>
                    {/* Admin-specific routes */}
                    {userRole === 'admin' && (
                      <>
                        <Route path="/" element={<Home navbarValues={navbarValues} />} />
                        <Route path="/productsearch" element={<Search />} />
                        <Route path="/knowledgebase" element={<RAGKnowledgebase />} />
                        <Route path="/schemamapper" element={<SchemaMapper />} />
                        <Route path="/InvisibleAI" element={<TwilioCallApp />} />
                        <Route path="/campaigns" element={<CampaignPage />} />
                        <Route path="/logs" element={<SessionLogs />} />
                        <Route path="/AnalyticsDasboard" element={<AnalyticsDashboard />} />
                        <Route path="/calculator" element={<CalculatorApp />} />
                        <Route path="/CampaignAnalytics" element={<Dashboard />} />
                        
                        {/* Fallback for admin: navigate to home if path not found */}
                        <Route path="*" element={<Navigate to="/" />} />
                      </>
                    )}

                    {/* Almajdouie-specific routes */}
                    {userRole === 'almajdouie' && (
                      <>
                        <Route path="/" element={<Home navbarValues={navbarValues} />} />
                        <Route path="/" element={<Navigate to="/Almajdouie_Analytics" replace />} />
                        {/* Specific redirects for Almajdouie */}
                        {/* <Route path="/CampaignAnalytics" element={<Navigate to="/Almajdouie_Analytics" replace />} />
                        <Route path="/AnalyticsDasboard" element={<Navigate to="/AlmajdouieCallAnalytics" replace />} /> */}
                        {/* Almajdouie's accessible dashboards */}
                        <Route path="/Almajdouie_Analytics" element={<AlmajdouieDashboard />} />
                        <Route path="/AlmajdouieCallAnalytics" element={<AlmajdouieCallDashboard />} />
                        {/* Fallback for Almajdouie: navigate to their default dashboard if path not found */}
                        <Route path="*" element={<Navigate to="/Almajdouie_Analytics" replace />} />
                        <Route path="/AlMajdouie_call" element={<AlMajdouieCallApp />} />
                        <Route path="/ServiceSlots" element={<ServiceSlotsPage />} />


                      </>
                    )}

                    {/* Lead Assistance-specific routes */}
                    {userRole === 'leadAssistance' && (
                      <>
                        <Route path="/" element={<Home navbarValues={navbarValues} />} />
                        <Route path="/" element={<Navigate to="/Lead_CallAnalytics" replace />} />

                        <Route path="/Lead_Assistant" element={<LeadCallApp />} />
                        <Route path="/Lead_CallAnalytics" element={<LeadAnalyticsDashboard />} />
                        <Route path="/Lead_Campaign_Analytics" element={<LeadDashboard />} />
                        {/* Fallback for Lead Assistance: navigate to home if path not found */}
                        <Route path="*" element={<Navigate to="/Lead_Campaign_Analytics" />} />
                      </>
                    )}

                    {/* Lead Assistance-specific routes */}
                    {userRole === 'PedalAssistance' && (
                      <>
                        <Route path="/" element={<Home navbarValues={navbarValues} />} />
                        <Route path="/" element={<Navigate to="/Lead_CallAnalytics" replace />} />

                        <Route path="/Pedal_Assistant" element={<PedalCallApp />} />
                        <Route path="/Pedal_CallAnalytics" element={<PedalAnalyticsDashboard />} />
                        <Route path="/Pedal_Campaign_Analytics" element={<PedalDashboard />} />
                        {/* Fallback for Lead Assistance: navigate to home if path not found */}
                        <Route path="*" element={<Navigate to="/Pedal_Campaign_Analytics" />} />
                      </>
                    )}
                    {/* Mai Dubai-specific routes */}
                    {userRole === 'MaiDubaiAssistance' && (
                      <>
                        <Route path="/" element={<Home navbarValues={navbarValues} />} />
                        <Route path="/MaiDubai_Assistant" element={<OrderCallApp />} />
                        <Route path="/MaiDubai_CallAnalytics" element={<CustomerAnalyticsDashboard />} />
                        <Route path="/" element={<Navigate to="/MaiDubai_CallAnalytics" replace />} />  
                        {/* Fallback for Mai Dubai Assistance: navigate to home if path not found */}
                        <Route path="*" element={<Navigate to="/MaiDubai_CallAnalytics" />} />
                      </>
                    )}                 

                    {/* If authenticated but userRole is somehow null (shouldn't happen with current login logic), redirect to login */}
                    {!userRole && <Route path="*" element={<Navigate to="/login" />} />}
                  </Routes>
                </main>
              </div>
            </div>
          ) : (
            // If not authenticated, only show the Login page
            <Routes>
              <Route path="/login" element={<Login onLogin={handleLogin} />} />
              {/* Redirect any other path to login if not authenticated */}
              <Route path="*" element={<Navigate to="/login" />} />
            </Routes>
          )}
          <Toaster />
        </BrowserRouter>
      </ToastProvider>
    </QueryClientProvider>
  );
};

export default App;